import React, { Fragment, useEffect, useState } from "react";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { useNavigate } from "react-router-dom";
import { TablePagination, IconButton, Collapse } from "@mui/material";
import {
  getCustomEmissionList,
  getSourceEmissionList,
} from "../../../services/emissionService";
import { checkPermissions, toastMessage } from "../../../helper";
import EmptyList from "../../common/EmptyList";
import { MODE } from "../../../constants/enums";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import SkeletonLoader from "../../common/Skeletons/TableSkeleton/TableSkeletonLoader";

const SourceEmissionList = () => {
  const { t, i18n } = useTranslation();
  const permissions = useSelector((state: any) => state.auth.userPermissions);
  const [sourceList, setCustomList] = useState([] as any);
  const [mode, setMode] = useState(MODE.PLAIN);
  const [customCount, setBunitsCount] = useState(0);
  const [controller, setController] = useState({
    page: 0,
    limit: 100,
  });
  const [detailedSelectIndex, setDetailedSelectIndex] = useState(0);
  const [detailedSelectItem, setDetailedSelectItem] = useState({} as any);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [typeInfo, setTypeInfo] = useState({
    typeName: "",
    methodName: "",
  });
  let cellColumns = [
    "Category",
    "Emission ID",
    "Value (kg CO2e)",
    "Duration",
    "Modified on",
  ];
  const navigate = useNavigate();
  useEffect(() => {
    const reqObj: any = {
      controller: controller,
    };
    setIsLoading(true);
    getSourceEmissionList(reqObj).then((res) => {
      const data = res.data;
      const newData = data.map((each: any) => {
        return {
          ...each,
          selected: false,
        };
      });
      setCustomList([...newData]);
      setDetailedSelectItem(() => data[detailedSelectIndex]);
      setIsLoading(false);
    });
  }, [controller, searchText]);

  const handlePageChange = (event: any, newPage: number) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (event: any) => {
    setController({
      ...controller,
      limit: parseInt(event.target.value, 10),
      page: 0,
    });
  };
  const navigateNext = (increment: number, pageIncrement: number) => {
    if (pageIncrement) {
      setController((prevState) => {
        return { ...prevState, page: prevState.page + pageIncrement };
      });
      setDetailedSelectIndex(() =>
        pageIncrement === -1 ? controller.limit - 1 : 0
      );
    } else if (increment) {
      setDetailedSelectItem(() => sourceList[detailedSelectIndex + increment]);
      setDetailedSelectIndex((prevIndex) => prevIndex + increment);
    }
  };
  const handleCollapse = (obj: any, index: number) => {
    const newList = sourceList.map((each: any, id: number) => {
      if (id === index) {
        return {
          ...each,
          selected: !each.selected,
        };
      }
      return each;
    });
    setCustomList(newList);
    const targetElementRef = document.getElementById(`row-${index}`);
    if (targetElementRef && index) {
      setTimeout(() => {
        targetElementRef.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  return (
    <Fragment>
      {!sourceList?.length && searchText === "" && !isLoading && (
        <div className="pw-empty-page flex flex-col h-100v w-full justify-center items-center ">
          <img className="pw-no-items" src="assets/images/empty.png" alt=" " />
          <div>{t("Welcome to Data Source")}</div>
          <div className="pw-bunit-desc">
            Organize and manage your company's carbon emissions by creating your
            first data source
          </div>
          {checkPermissions(["CREATE_EMISSION_FACTOR"], permissions) ? (
            <div>
              {/* <AddBoxIcon className="pw-box-icon" /> <span>{t("New")}</span> */}
            </div>
          ) : (
            ""
          )}
        </div>
      )}

      {(sourceList?.length || searchText !== "" || isLoading) &&
      mode === MODE.PLAIN ? (
        <div className="pw-blist-main overflow-y-auto h-full w-full">
          <div className="pw-blist-header w-full flex flex-row justify-end"></div>
          {isLoading ? (
            <SkeletonLoader type="text" cells={cellColumns} />
          ) : (
            <Fragment>
              {sourceList?.length ? (
                <div className="h-80v pw-hide-scroll overflow-y-auto">
                  {sourceList.map((eachList: any, mainIndex: number) => {
                    return (
                      <table className="w-full border-collapse">
                        <thead className="pw-custom-head">
                          <tr id={`row-${mainIndex}`}>
                            <th
                              className="text-sm text-neutral-600 border border-neutral-200 p-2 items-center w-full flex justify-between pw-cursor-pointer"
                              colSpan={3}
                              onClick={() =>
                                handleCollapse(eachList, mainIndex)
                              }
                            >
                              {eachList.category_code} {eachList.category_name}
                              <IconButton
                                className="pw-collapse-icon"
                                aria-label="expand row"
                                size="small"
                                onClick={() =>
                                  handleCollapse(eachList, mainIndex)
                                }
                              >
                                {eachList.selected ? (
                                  <ArrowDropUpIcon />
                                ) : (
                                  <ArrowDropDownIcon />
                                )}
                              </IconButton>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <Collapse
                            in={eachList.selected}
                            timeout="auto"
                            unmountOnExit
                          >
                            <tr>
                              {eachList.data
                                .sort((a: any, b: any) => {
                                  return a.name.localeCompare(b.name);
                                })
                                .map((eachData: any, index: number) => {
                                  return (
                                    <tr>
                                      <td
                                        className="border border-neutral-200 p-3 pw-e2e-categorytype bg-white"
                                        style={{ width: "25rem" }}
                                      >
                                        {eachData.name}
                                      </td>

                                      {eachData.subtypes
                                        .sort((a: any, b: any) => {
                                          return a.name.localeCompare(b.name);
                                        })
                                        .map((each: any) => {
                                          return (
                                            <tr key={eachData.id}>
                                              <td
                                                className="border border-neutral-200 p-3 pw-e2e-categorytype bg-white"
                                                style={{ width: "50%" }}
                                              >
                                                {each.name}
                                              </td>
                                              <td className="border border-neutral-200 pw-e2e-categorymethod bg-white w-50 ">
                                                {each.methods
                                                  .sort((a: any, b: any) => {
                                                    return a.name.localeCompare(
                                                      b.name
                                                    );
                                                  })
                                                  .map(
                                                    (
                                                      eachMethod: any,
                                                      subId: number
                                                    ) => {
                                                      return (
                                                        <tr key={eachMethod.id}>
                                                          <td
                                                            style={{
                                                              width: "40rem",
                                                              borderBottom:
                                                                subId ===
                                                                each.methods
                                                                  .length -
                                                                  1
                                                                  ? "none"
                                                                  : "1px solid #EAEEF4",
                                                            }}
                                                            className="p-2"
                                                          >
                                                            <ol className="list-disc ml-4 ">
                                                              <li>
                                                                {
                                                                  eachMethod.name
                                                                }
                                                              </li>
                                                            </ol>
                                                          </td>
                                                        </tr>
                                                      );
                                                    }
                                                  )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tr>
                                  );
                                })}
                            </tr>
                          </Collapse>
                          {/* )} */}
                        </tbody>
                      </table>
                    );
                  })}
                </div>
              ) : (
                <EmptyList />
              )}
            </Fragment>
          )}
          {customCount >= 10 && (
            <TablePagination
              component="div"
              onPageChange={handlePageChange}
              page={controller.page}
              count={customCount}
              rowsPerPage={controller.limit}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </div>
      ) : (
        ""
      )}
    </Fragment>
  );
};

export default SourceEmissionList;
